import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import { useSearchParams } from '@remix-run/react';
import type { Recommendation } from '@shared/dream-components/src';
import { v4 as uuidv4 } from 'uuid';

import useEmail from '~/hooks/useEmail';
import useRecommendations from '~/hooks/useLoaders/useRecommendations';
import { trackRecommendationImpressions } from '~/services/api/recommendations/trackImpressions';

import useGlobalModals from '../GlobalModals/hooks/useGlobalModals';

type RenderProps = {
  email?: string;
  isOpen: boolean;
  isLoading: boolean;
  recommendations: Recommendation[];
  submittedIds: string[];
  onSubmitSuccess: (recommendations: Recommendation[]) => void;
};

type Props = {
  onOpenChange: (value: boolean) => void;
  children: (props: RenderProps) => ReactNode;
};

const DESIRED_RECOMMENDATIONS_COUNT = 6;

const getImpressionToken = (tokens: Record<string, string>, recommendationId: string) => {
  const existingToken = tokens[recommendationId];
  if (existingToken) return existingToken;

  const token = uuidv4();
  tokens[recommendationId] = token;
  return token;
};

const RecommendationsModalContext = ({ onOpenChange, children }: Props) => {
  const [email] = useEmail();
  const { isLoading, recommendations, boostedRecommendations } = useRecommendations();
  const [submittedIds, setSubmittedIds] = useState<string[]>([]);
  const { currentModal } = useGlobalModals();
  const isOpen = currentModal === 'recommendations';
  const [searchParams] = useSearchParams();
  const subscriberId = searchParams.get('subscriber_id') ?? undefined;
  const subscriptionUuid = searchParams.get('sid') ?? undefined;
  const impressionsTracked = useRef(false);
  const impressionTokens = useRef<Record<string, string>>({});

  // Grab the correct number of recommendations to display (top 4 + boosts up to 6 total)
  // then transform them to the correct structure for the modal
  const visibleRecommendations = useMemo(() => {
    const safeRecommendations = Array.isArray(recommendations) ? recommendations : [];
    const safeBoosted = Array.isArray(boostedRecommendations) ? boostedRecommendations : [];

    return safeRecommendations
      .slice(0, 4)
      .reverse()
      .concat([...safeBoosted].reverse())
      .slice(0, DESIRED_RECOMMENDATIONS_COUNT)
      .reverse()
      .map((rec) => ({
        id: rec.id,
        impression_token: getImpressionToken(impressionTokens.current, rec.id),
        name: rec.publication?.name ?? '',
        description: rec.publication?.description ?? '',
        logo_url: rec.publication?.thumbnail_image_url ?? '',
        site_url: rec.publication?.site_url ?? '',
        sponsored: rec.boosted ?? false,
        recommended_publication_id: rec.recommended_publication_id ?? null,
        isOwner: false,
        badge: rec.badge,
      }));
  }, [recommendations, boostedRecommendations]);

  const onSubmitSuccess = (recommendations: Recommendation[]) => {
    setSubmittedIds((prev) => [...prev, ...recommendations.map((r) => r.id)]);
  };

  useEffect(() => {
    const noRecs = visibleRecommendations.length === 0;
    const subscribedToAll = new Set(submittedIds).size === visibleRecommendations.length;

    if (isOpen && !isLoading && (noRecs || subscribedToAll)) onOpenChange(false);
  });

  useEffect(() => {
    if (isOpen && !isLoading && visibleRecommendations.length > 0 && !impressionsTracked.current) {
      impressionsTracked.current = true;
      trackRecommendationImpressions(visibleRecommendations, subscriberId, subscriptionUuid).catch(() => null);
    }
    if (!isOpen) {
      impressionsTracked.current = false;
    }
  }, [isOpen, isLoading, visibleRecommendations, subscriberId, subscriptionUuid]);

  return children({
    email,
    isOpen,
    isLoading,
    recommendations: visibleRecommendations,
    submittedIds,
    onSubmitSuccess,
  });
};

export default RecommendationsModalContext;
